import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Info,
    Gauge, Battery, Lightbulb, Shield
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASIFICACIONES = {
    fatiga: { 
        label: 'Fatiga', 
        color: 'bg-[#E8B27A]', 
        textColor: 'text-[#E8B27A]',
        description: 'Tu nivel de energía es bajo. Puede que necesites descanso físico o mental antes de abordar la tarea.'
    },
    saturacion: { 
        label: 'Saturación', 
        color: 'bg-[#D07F7E]', 
        textColor: 'text-[#D07F7E]',
        description: 'Tu mente está sobrecargada. Hay demasiadas cosas que procesar y esto genera bloqueo.'
    },
    evitacion: { 
        label: 'Evitación', 
        color: 'bg-[#7A9BA8]', 
        textColor: 'text-[#7A9BA8]',
        description: 'Hay resistencia emocional hacia la tarea. Puede haber miedo, ansiedad o incomodidad asociada.'
    },
    mixto: { 
        label: 'Estado Mixto', 
        color: 'bg-[#3E5F6E]', 
        textColor: 'text-[#3E5F6E]',
        description: 'Es una combinación de factores. Esto es común y requiere abordar múltiples aspectos.'
    }
};

export default function Modulo3Semaforo() {
    const navigate = useNavigate();
    const [energia, setEnergia] = useState([5]);
    const [claridad, setClaridad] = useState([5]);
    const [resistencia, setResistencia] = useState([5]);
    const [reflexion, setReflexion] = useState('');
    const [clasificacion, setClasificacion] = useState(null);
    const [clasificacionManual, setClasificacionManual] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    // Calculate suggested classification based on slider values
    const calculateClasificacion = useCallback(() => {
        const e = energia[0];
        const c = claridad[0];
        const r = resistencia[0];
        
        // Classification rules
        if (e <= 3 && c > 5 && r <= 4) {
            return 'fatiga';
        } else if (c <= 3 && e > 4) {
            return 'saturacion';
        } else if (r >= 7 && e > 4 && c > 4) {
            return 'evitacion';
        } else {
            return 'mixto';
        }
    }, [energia, claridad, resistencia]);

    useEffect(() => {
        setClasificacion(calculateClasificacion());
    }, [calculateClasificacion]);

    // Load draft on mount
    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/semaforo_funcional`, {
                    withCredentials: true
                });
                if (data.data) {
                    if (data.data.energia) setEnergia([data.data.energia]);
                    if (data.data.claridad) setClaridad([data.data.claridad]);
                    if (data.data.resistencia) setResistencia([data.data.resistencia]);
                    if (data.data.reflexion) setReflexion(data.data.reflexion);
                    if (data.data.clasificacionManual) setClasificacionManual(data.data.clasificacionManual);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    // Auto-save draft
    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { 
                    module_id: 'semaforo_funcional', 
                    data: { 
                        energia: energia[0], 
                        claridad: claridad[0], 
                        resistencia: resistencia[0],
                        reflexion,
                        clasificacionManual
                    } 
                },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [energia, claridad, resistencia, reflexion, clasificacionManual]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [energia, claridad, resistencia, reflexion, clasificacionManual, saveDraft, loadingDraft, readOnly]);

    const finalClasificacion = clasificacionManual || clasificacion;
    const canSubmit = !readOnly && (reflexion.trim().length > 0 && finalClasificacion);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa la reflexión antes de enviar');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/semaforo/submit`,
                { 
                    energia: energia[0],
                    claridad: claridad[0],
                    resistencia: resistencia[0],
                    reflexion,
                    clasificacion_sugerida: clasificacion,
                    clasificacion_final: finalClasificacion
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado exitosamente');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar el módulo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-lg animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-[#5FAF9D] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-4">¡Semáforo completado!</h2>
                        
                        {/* Classification result */}
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${CLASIFICACIONES[finalClasificacion].color} bg-opacity-20 mb-4`}>
                            <div className={`w-3 h-3 rounded-full ${CLASIFICACIONES[finalClasificacion].color}`}></div>
                            <span className={`font-medium ${CLASIFICACIONES[finalClasificacion].textColor}`}>
                                {CLASIFICACIONES[finalClasificacion].label}
                            </span>
                        </div>
                        
                        {aiInsight && (
                            <div className="senda-ai-panel text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Tu perfil funcional</p>
                                        <p className="text-[#1F2A30] italic">"{aiInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <p className="text-[#4A5B63] mb-6">
                            Has identificado tu estado interno dominante. Esta información te ayudará a tomar decisiones más acertadas sobre cómo abordar tus tareas.
                        </p>
                        
                        <div className="mb-6"><ModuleFeedback moduleId="semaforo" chapter={1} /></div>
                        <Link to="/usuario">
                            <Button className="senda-btn-primary" data-testid="back-to-dashboard-semaforo">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]" data-testid="back-to-usuario-semaforo">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Semáforo funcional</h1>
                                <p className="text-sm text-[#4A5B63]">Identifica tu estado interno</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Guardando...
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="senda-module-container animate-fade-in-up">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#3E5F6E] rounded-xl flex items-center justify-center">
                                <Gauge className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#3E5F6E]">Tu estado actual</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Evalúa tu energía, claridad y resistencia</p>
                            </div>
                        </div>
                        
                        <div className="p-4 bg-[#F5F7F8] rounded-lg border border-[#DCE3E8] flex items-start gap-3">
                            <Info className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                            <p className="text-sm text-[#4A5B63]">
                                Mueve los controles deslizantes para indicar cómo te sientes ahora mismo. 
                                No hay respuestas correctas o incorrectas, solo tu percepción honesta.
                            </p>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-8">
                        {/* Energía Slider */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Battery className="h-5 w-5 text-[#E8B27A]" />
                                <Label className="senda-label mb-0">Energía física y mental</Label>
                                <span className="ml-auto text-2xl font-semibold text-[#1F2A30]">{energia[0]}</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={energia}
                                    onValueChange={setEnergia}
                                    max={10}
                                    min={0}
                                    step={1}
                                    className="w-full"
                                    data-testid="energia-slider"
                                />
                            </div>
                            <div className="flex justify-between text-xs text-[#7A8A93]">
                                <span>0 - Agotado/a</span>
                                <span>10 - Muy energizado/a</span>
                            </div>
                        </div>

                        {/* Claridad Slider */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Lightbulb className="h-5 w-5 text-[#5FAF9D]" />
                                <Label className="senda-label mb-0">Claridad mental</Label>
                                <span className="ml-auto text-2xl font-semibold text-[#1F2A30]">{claridad[0]}</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={claridad}
                                    onValueChange={setClaridad}
                                    max={10}
                                    min={0}
                                    step={1}
                                    className="w-full"
                                    data-testid="claridad-slider"
                                />
                            </div>
                            <div className="flex justify-between text-xs text-[#7A8A93]">
                                <span>0 - Muy confuso/a</span>
                                <span>10 - Mente muy clara</span>
                            </div>
                        </div>

                        {/* Resistencia Slider */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Shield className="h-5 w-5 text-[#7A9BA8]" />
                                <Label className="senda-label mb-0">Resistencia emocional</Label>
                                <span className="ml-auto text-2xl font-semibold text-[#1F2A30]">{resistencia[0]}</span>
                            </div>
                            <div className="px-2">
                                <Slider
                                    value={resistencia}
                                    onValueChange={setResistencia}
                                    max={10}
                                    min={0}
                                    step={1}
                                    className="w-full"
                                    data-testid="resistencia-slider"
                                />
                            </div>
                            <div className="flex justify-between text-xs text-[#7A8A93]">
                                <span>0 - Sin resistencia</span>
                                <span>10 - Mucha resistencia</span>
                            </div>
                        </div>

                        {/* Classification Result */}
                        <div className="p-6 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                            <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-3">Clasificación sugerida</p>
                            <div className="flex flex-wrap gap-3">
                                {Object.entries(CLASIFICACIONES).map(([key, value]) => (
                                    <button
                                        key={key}
                                        onClick={() => setClasificacionManual(clasificacionManual === key ? null : key)}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all ${
                                            (clasificacionManual || clasificacion) === key
                                                ? `${value.color} bg-opacity-20 border-current ${value.textColor}`
                                                : 'border-[#DCE3E8] text-[#7A8A93] hover:border-[#3E5F6E]'
                                        }`}
                                        data-testid={`clasificacion-${key}`}
                                    >
                                        <div className={`w-3 h-3 rounded-full ${value.color}`}></div>
                                        <span className="font-medium">{value.label}</span>
                                        {clasificacion === key && !clasificacionManual && (
                                            <span className="text-xs">(sugerido)</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {finalClasificacion && (
                                <p className="mt-4 text-sm text-[#4A5B63]">
                                    {CLASIFICACIONES[finalClasificacion].description}
                                </p>
                            )}
                        </div>

                        {/* Reflexión */}
                        <div className="space-y-2">
                            <Label className="senda-label">Reflexión breve</Label>
                            <Textarea
                                value={reflexion}
                                onChange={(e) => setReflexion(e.target.value)}
                                placeholder="¿Qué crees que está influyendo en tu estado actual? ¿Hay algo específico que lo esté causando?"
                                className="senda-input min-h-[100px]"
                                data-testid="reflexion-semaforo"
                            />
                            <p className="text-xs text-[#7A8A93]">Escribe tu reflexión</p>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="senda-btn-primary"
                                data-testid="submit-semaforo-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar módulo
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
